import { NmLoader } from '../NmLoader';
import { primaryActionStyle } from '../primaryActionStyle';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BLUE, PINKL } from './palette';
import {
  buildMetricsSettingPayload,
  mergeMetricsChartSettings,
  usesProfileMetrics,
  type MetricsChartSettings,
} from './metricsChartSettings';
import { forms, metricsApi, type MetricTypeOption } from '../../services/api';

interface MetricsChartSettingsPanelProps {
  open: boolean;
  userId: number;
  setting: Record<string, unknown>;
  chartTitle?: string;
  saving?: boolean;
  onClose: () => void;
  onSave: (patch: MetricsChartSettings) => void;
}

export function MetricsChartSettingsPanel({
  open,
  userId,
  setting,
  chartTitle,
  saving,
  onClose,
  onSave,
}: MetricsChartSettingsPanelProps) {
  const { t } = useTranslation();
  const merged = useMemo(() => mergeMetricsChartSettings(setting), [setting]);

  const [useProfile, setUseProfile] = useState(() => usesProfileMetrics(merged.selected_metrics));
  const [picked, setPicked] = useState<number[]>(() =>
    usesProfileMetrics(merged.selected_metrics) ? [] : (merged.selected_metrics ?? []),
  );
  const [types, setTypes] = useState<MetricTypeOption[]>([]);
  const [profileNames, setProfileNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const next = mergeMetricsChartSettings(setting);
    setUseProfile(usesProfileMetrics(next.selected_metrics));
    setPicked(
      usesProfileMetrics(next.selected_metrics) ? [] : (next.selected_metrics ?? []),
    );
  }, [open, setting]);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([
      metricsApi.listTypes(),
      forms.getUserMetrics(userId),
    ])
      .then(([typesRes, profileRes]) => {
        if (cancelled) return;
        const list = (typesRes.data as { data?: { metrics?: MetricTypeOption[] } })?.data?.metrics ?? [];
        setTypes(list);
        const profile = (profileRes.data as { data?: { metrics?: Array<{ name?: string }> } })?.data?.metrics ?? [];
        setProfileNames(profile.map((m) => m.name ?? '').filter(Boolean));
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('charts.metricsSettings.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId, t]);

  if (!open) return null;

  function toggleMetric(id: number) {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function handleSave() {
    onSave(buildMetricsSettingPayload(useProfile, picked));
  }

  const canSave = useProfile || picked.length > 0;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(0,0,0,.4)' }}
      />
      <div style={PANEL_STYLE} className="ltr-island">
        <div style={HEADER_STYLE}>
          <div>
            <div style={EYE_STYLE}>{t('charts.metricsSettings.eye')}</div>
            <div style={TITLE_STYLE}>{chartTitle ?? t('charts.metricsSettings.title')}</div>
          </div>
          <button type="button" onClick={onClose} style={CLOSE_BTN} aria-label={t('common.close')}>
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px 24px' }}>
          <div style={SECTION_LABEL}>{t('charts.metricsSettings.sectionSource')}</div>

          <label style={ROW_STYLE}>
            <input
              type="radio"
              name="metrics-source"
              checked={useProfile}
              onChange={() => setUseProfile(true)}
              style={{ accentColor: 'var(--palette-brand-ink)', marginTop: 3 }}
            />
            <span style={{ flex: 1 }}>
              <div style={ROW_TITLE}>{t('charts.metricsSettings.useProfile')}</div>
              <div style={ROW_SUB}>
                {profileNames.length
                  ? t('charts.metricsSettings.profileHint', { names: profileNames.join(' · ') })
                  : t('charts.metricsSettings.profileHintEmpty')}
              </div>
            </span>
          </label>

          <label style={{ ...ROW_STYLE, marginTop: 8 }}>
            <input
              type="radio"
              name="metrics-source"
              checked={!useProfile}
              onChange={() => setUseProfile(false)}
              style={{ accentColor: 'var(--palette-brand-ink)', marginTop: 3 }}
            />
            <span style={{ flex: 1 }}>
              <div style={ROW_TITLE}>{t('charts.metricsSettings.pickCustom')}</div>
              <div style={ROW_SUB}>{t('charts.metricsSettings.pickCustomHint')}</div>
            </span>
          </label>

          {!useProfile && (
            <>
              <div style={{ ...SECTION_LABEL, marginTop: 20 }}>{t('charts.metricsSettings.sectionMetrics')}</div>
              {loading && <div style={HINT_STYLE}><NmLoader size="sm" inline label={t('charts.metricsSettings.loading')} /></div>}
              {loadError && <div style={{ ...HINT_STYLE, color: '#ef4444' }}>{loadError}</div>}
              {!loading && !loadError && types.length === 0 && (
                <div style={HINT_STYLE}>{t('charts.metricsSettings.noTypes')}</div>
              )}
              {types.map((mt) => (
                <label key={mt.id} style={ROW_STYLE}>
                  <input
                    type="checkbox"
                    checked={picked.includes(mt.id)}
                    onChange={() => toggleMetric(mt.id)}
                    style={{ accentColor: 'var(--palette-brand-ink)', marginTop: 3 }}
                  />
                  <span style={ROW_TITLE}>{mt.name}</span>
                </label>
              ))}
              {!useProfile && picked.length === 0 && !loading && (
                <div style={{ ...HINT_STYLE, color: '#f97316' }}>{t('charts.metricsSettings.pickOne')}</div>
              )}
            </>
          )}
        </div>

        <div style={FOOTER_STYLE}>
          <button type="button" onClick={onClose} style={CANCEL_BTN} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !canSave}
            style={{ ...SAVE_BTN, opacity: saving || !canSave ? 0.6 : 1 }}
          >
            {saving ? t('common.saving') : t('charts.metricsSettings.save')}
          </button>
        </div>
      </div>
    </>
  );
}

const PANEL_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 400,
  maxWidth: '100vw',
  zIndex: 211,
  background: 'var(--surface-0)',
  boxShadow: '-8px 0 40px rgba(0,0,0,.45)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: '24px 20px 16px',
  borderBottom: '1px solid var(--surface-sunken)',
  flexShrink: 0,
};

const EYE_STYLE: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  marginBottom: 4,
};

const TITLE_STYLE: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 800,
  color: 'var(--fg-1)',
};

const CLOSE_BTN: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 9,
  border: 'none',
  cursor: 'pointer',
  background: 'var(--surface-sunken)',
  color: 'var(--fg-3)',
  fontSize: 18,
};

const SECTION_LABEL: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  marginBottom: 10,
};

const ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 0',
  cursor: 'pointer',
};

const ROW_TITLE: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--fg-1)',
};

const ROW_SUB: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-4)',
  marginTop: 2,
  lineHeight: 1.45,
};

const HINT_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-4)',
  padding: '8px 0',
};

const FOOTER_STYLE: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  padding: '14px 20px 20px',
  borderTop: '1px solid var(--surface-sunken)',
  flexShrink: 0,
};

const CANCEL_BTN: React.CSSProperties = {
  flex: 1,
  padding: '10px 16px',
  borderRadius: 12,
  border: 'none',
  cursor: 'pointer',
  background: 'var(--surface-sunken)',
  color: 'var(--fg-2)',
  fontWeight: 700,
  fontSize: 12,
};

const SAVE_BTN: React.CSSProperties = {
  flex: 2,
  padding: '10px 16px',
  borderRadius: 12,
  border: 'none',
  cursor: 'pointer',
  ...primaryActionStyle,
  fontWeight: 700,
  fontSize: 12,
  boxShadow: primaryActionStyle.boxShadow,
};
